import React, { useEffect, useState, useContext } from 'react';
import { SVBTypography } from '@svb/svb-react-components';
import { detailsContext } from './context/DetailsProvider';
import SolutionImagesMD from './SolutionImagesMD';
import Markdown from 'markdown-to-jsx';

// IMPORTANT INFO
// Component is expecting an array of objects, the array must be wrapped in double quotes " on the md file
// All of the double quotes " INSIDE THE cURL (rendered doubled quotes) must be replaced by a backslach followed by a backtick \`
// The double quotes that correspond to the actual step object must be replaced by backticks `
// If a backslash needs to be rendered inside the cURL, a backslash must be placed before the actual backslash \\

// Example for the following cURL:
//
// curl https://api.svb.com/ v2/event/subscriptions \
// -X POST \
// -H 'authorization': 'Bearer xxxxxxxxxxx'
// -H 'x-jws-signature: xxxxxxxxxxx' \
// -H 'X-SVB-Correlation-ID: 123456' \
// -H 'x-idempotency-key: 123456' \
// -H 'prefer: RETURN_MINIMAL' \
// -H 'content-type: application/json' \
// -d '{
// "callback_url": "https://www.somewhere.com/svb-subscriptions",
// "client_secret_key": "9a12cfc0-0458-11ec-b25c-acde48001124",
// "event_type_names": [
// "wires.status"
// ],
// "status": "ACTIVE"
// }'
//
// Steps array must be sent in the md file as the following:
//
// "[{
//   `title`:`Step 3: (optional) Subscribe to Wires Webhooks`,
//   `description`:`Initiate a POST request to the /v2/event/subscriptions endpoint to subscribe to desired Wires webhook events, such as the Status event that will send a notification for status changes Wire payments`,
//   `languages`:[`cURL`, `Ruby`, `Python`, `C#`, `Java`, `PHP`, `Node.js`],
//   `codeLines`:[{
//     `highlightedText`:`curl https://api.svb.com/ v2/event/subscriptions \\`
//   },{
//     `highlightedText`:`-X`,
//     `text`:`POST \\`
//   },{
//     `highlightedText`:`-H`,
//     `text`:`'authorization': 'Bearer xxxxxxxxxxx'`
//   },{
//     `highlightedText`:`-H`,
//     `text`:`'x-jws-signature: xxxxxxxxxxx' \\`
//   },{
//     `highlightedText`:`-H`,
//     `text`:`'X-SVB-Correlation-ID: 123456' \\`
//   },{
//     `highlightedText`:`-H`,
//     `text`:`'x-idempotency-key: 123456' \\`
//   },{
//     `highlightedText`:`-H`,
//     `text`:`'prefer: RETURN_MINIMAL' \\`
//   },{
//     `highlightedText`:`-H`,
//     `text`:`'content-type: application/json' \\`
//   },{
//     `highlightedText`:`-d`,
//     `text`:`'{`
//   },{
//     `text`:`\`callback_url\`: \`https://www.somewhere.com/svb-subscriptions\`,`
//   },{
//     `text`:`\`client_secret_key\`: \`9a12cfc0-0458-11ec-b25c-acde48001124\`,`
//   },{
//     `text`:`\`event_type_names\`: [`
//   },{
//     `text`:`\`wires.status\``
//   },{
//     `text`:`],`
//   },{
//     `text`:`\`status\`: \`ACTIVE\``
//   },{
//     `text`:`}'`
//   }]
//   }]"

const SolutionDetailsTechnicalGuide = ({ title, ...props }) => {
  const [mounted, setMounted] = useState(false);
  const { addTitle, idGenerator } = useContext(detailsContext);

  useEffect(() => {
    setMounted(true);
  });

  useEffect(() => {
    addTitle(title);
  }, [title]);

  let steps = props.steps || "[]";
  steps = steps.replace(/`/g, '"');
  // console.log(steps.slice(0, 4742));
  steps = JSON.parse(steps);
  let tabCounter = 0;

  if (!mounted) {
    return <></>;
  }
  /* eslint-disable */
  return (
    <section className="solutions_solutionDetails_techGuide_wrapper solution-technical-guide-section" id={idGenerator(title)}>
      <div className="solution-technical-guide-content">
        <div className="solution-technical-guide-content-title">
          <SVBTypography type="heading-large" variant="mb-4">{title}</SVBTypography>
        </div>
        {
          steps.map((step, index) => <div key={`step#${index}`} className="solution-technical-guide-content-bottom">
            <SVBTypography type="sub-heading">{step.title}</SVBTypography>
            <div className="solution-technical-guide-content-bottom-description">
              <SVBTypography type="paragraph" variant="descriptionImg">
                <Markdown
                  options={{
                    overrides: {
                      img: {
                        component: SolutionImagesMD,
                        props: props,
                      }
                    },
                  }}
                >{step.description}
                </Markdown>
              </SVBTypography>

            </div>
            {step.subHeader && <span className="solution-technical-guide-content-bottom-example">{step.subHeader}</span>}
            {(step && step.languages) && (step && step.codeLines) ? (
            <div className="code_snippet code_snippet--dark api-explorer-snippet mt-3">
              <div className="code_snippet_header">
                <div className="code_snippet_toolbar">
                  <div className="d-flex align--center gap--8">
                    <div className="dropdown">
                      <button className="dropdown_trigger dropdown--snippet dropdown--snippet--inline">
                        {step && step.languages && step.languages[0]}
                        <span className="dropdown_indicator"></span>
                      </button>
                      <ul className="list dropdown_menu dropdown_menu--snippet dropdown_menu--snippet--inline">
                        {
                           step && step.languages && step.languages.map(language => <li key={`language-${language}`} className="dropdown_option">{language}</li>)
                        }
                      </ul>
                    </div>
                  </div>

                </div>
              </div>
              <ol className="code_snipped_code">
                {
                step && step.codeLines && step.codeLines.map((line, index) => {
                    if (line.text?.includes("}") || line.text?.includes("]")) tabCounter--;
                    const returnValue = <li key={`line#${index}`} className={tabCounter ? `tab--${tabCounter}` : ""}>
                      <div>
                        {line.highlightedText && <span className="token token--highlight">{line.highlightedText + " "}</span>}
                        {line.text && <span className="token token--string">{line.text}</span>}
                      </div>
                    </li>
                    if (line.text?.includes("{") || line.text?.includes("[")) tabCounter++;
                    return returnValue;
                  })
                }
              </ol>
            </div>
            ) : '' }
          </div>)
        }
      </div>
    </section>
  );
};
/* eslint-enable */
export default SolutionDetailsTechnicalGuide;
